import { useQuery } from "@tanstack/react-query";
import { fetchApiPost } from "../utils";

// ============================================================================
// Request/Response Types
// ============================================================================

export interface GenerateRiskSummaryRequest {
  crop_year: number;
  excluded_customer_ids?: string[];
  customer_ids?: string[];
  trader_ids?: string[];
  commodity_types?: string[];
}

export interface RiskSummaryResponse {
  crop_year: number;
  available_crop_years: number[];
  profiles: TaggedProfile[];
  crop_settings: RiskCropSettingResponse[];
  rows: TaggedRiskRow[];
}

// ============================================================================
// Profile Types
// ============================================================================

export interface TaggedProfile {
  id: string;
  customer_id: string;
  commodity_type: string;
  commodity_group: string;
  crop_year: number;

  // Production data
  acreage: string;
  projected_yield: string;
  actual_yield: string;
  projected_total_production: string;
  actual_total_production: string;
  cost_per_acre: string;

  // Implied values from price enrichment (may be null)
  implied_basis: string | null;
  implied_futures: string | null;
  implied_futures_contract: string | null;
  implied_fx_futures: string | null;
  implied_fx_futures_contract: string | null;

  // Derived values
  effective_production: string;
  effective_yield: string;
}

export interface RiskCropSettingResponse {
  id: string;
  customer_id: string | null;
  commodity_name: string;
  commodity_type: string;
  crop_year: number;
  option_month: string | null;
  cost_per_acre: number | null;
  implied_basis: number | null;
  is_enabled: boolean;
}

// ============================================================================
// Row Types
// ============================================================================

export interface TaggedRiskRow {
  id: string;
  row_type: "release" | "position";

  // Common fields
  customer_id: string | null;
  commodity_type: string;
  crop_year: number;
  quantity: string | null;
  quantity_uom: string;

  // Pricing
  price: string | null;
  futures: string | null;
  basis: string | null;

  // Implied values
  implied_futures: string | null;
  implied_basis: string | null;
  implied_fx_futures: string | null;
  implied_futures_contract: string | null;
  implied_fx_futures_contract: string | null;

  // Revenue value
  revenue_value: string | null;

  // Meta tags
  meta: RiskRowMeta;

  // Type-specific data
  release_data: ReleaseData | null;
  position_data: PositionData | null;

  // OTC contract detail
  detailed_contract_info: DetailedContractInfo | null;
}

export interface RiskRowMeta {
  // Commodity grouping
  commodity_group: string;
  commodity_display_name: string;

  // Sold/unsold classification
  is_cash: boolean;
  is_futures_only: boolean;
  is_basis_only: boolean;
  is_unpriced: boolean;

  // Contract type tags (releases only)
  is_otc: boolean;
  is_hta: boolean;
  is_beacon: boolean;
  is_dp: boolean;
  is_dpq: boolean;
  is_direct_sale: boolean;

  // Double-up
  has_double_up: boolean;
  double_up_contract_number: string | null;

  // Position classification (positions only)
  is_hedgeable: boolean;
  hedgeable_ignore_reason: string | null;
  is_open_offer: boolean;
  is_realized: boolean;
  is_brokerage_position: boolean;
  contributes_to_futures_hedging: boolean;
  contributes_to_physical_hedging: boolean;

  position_source_type: string | null;
  brokerage_type: string | null;
  brokerage_provider_type: string | null;
  brokerage_group_type: string | null;
  instrument_type: string | null;

  // Futures/Physical breakdown contribution
  futures_contribution_type: string | null;
  physical_contribution_type: string | null;

  // Data quality
  has_implied_values: boolean;
  missing_price_data: boolean;

  // FX
  requires_fx_conversion: boolean;
  fx_commodity: string | null;
}

export interface ReleaseData {
  contract_type: string;
  contract_number: string;
  contract_release: string;
  option_month: string | null;

  start_date: string;
  end_date: string;

  status: string;
  pr_status: string;

  loads: string | null;
  note: string | null;

  // Location / zone
  location_id: string | null;
  market_zone_id: string | null;
  report_market_zone_id: string | null;
  trader_id: string | null;

  // Source info
  source_type: string | null;
}

export interface PositionData {
  trading_account: string;
  trading_account_id: string;

  long_qty: number;
  short_qty: number;
  net_position: string;

  put_or_call: string | null;
  strike_price: string | null;
  premium: string;

  close_price: string | null;
  trade_price: string | null;
  mark_to_market: string | null;
  original_realized_amount: string | null;
  currency_adjusted_realized_amount: string | null;

  contract_description: string | null;
  symbol_code: string | null;
  option_month: string | null;

  enabled: boolean;
  source_type: string;
  note: string | null;
}

export interface DetailedContractInfo {
  source: "trading_contract" | "general_contract";
  contract_id: string;
  contract_type: string;
  contract_type_label: string;

  crop_year: number;

  // OTC leg info (trading_contract only)
  step_one: string | null;
  step_two: string | null;
  step_three: string | null;
  double_up: string | null;
  leg_labels: Record<string, string>;

  note: string | null;

  // General contract specific (general_contract only)
  comments: string | null;
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to generate risk summary V3
 * 
 * @example
 * const { data, isLoading, refetch } = useGenerateRiskSummaryV3({
 *   crop_year: 2024,
 *   customer_ids: ['customer-123'],
 * });
 */
export function useGenerateRiskSummaryV3(
  request: GenerateRiskSummaryRequest,
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["risk", "v3", "summary", request],
    queryFn: () => fetchApiPost<RiskSummaryResponse>("risk/v3/summary", request),
    enabled: options?.enabled ?? true,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}
